// File: @/config/sites.ts

/**
 * Multi-domain campaign registry.
 *
 * One codebase serves several campaign domains that share identical content
 * except for headlines and domain-specific strings. Each Vercel project sets
 * NEXT_PUBLIC_SITE_ID at build time, so every domain is still fully static —
 * no per-request host detection.
 */

export type SiteId = 'areyouprotected' | 'immunenotinvisible' | 'answersstarthere';

export interface SiteConfig {
  readonly id: SiteId;
  /** Canonical origin without trailing slash */
  readonly url: string;
  /** Mixed-case domain used in visible copy, e.g. footer attribution */
  readonly displayName: string;
  /** Home-screen name (web app manifest, iOS) */
  readonly appName: string;
  /** PWA short_name (keep ≤ 15 chars to avoid truncation on home screens) */
  readonly appShortName: string;
  /** Intro headline at the top of the page */
  readonly pageHeader: string;
  /** Headline above the 10 Warning Signs lists */
  readonly warningSignsHeader: string;
  /** Browser tab / search result title */
  readonly pageTitle: string;
  /** Meta description for search results */
  readonly pageDescription: string;
  /** Description used on social share cards */
  readonly ogDescription: string;
  /** 1200×630 social share image, served from public/ */
  readonly ogImage: string;
}

const PAGE_TITLE_SUFFIX = 'Primary Immunodeficiency Awareness';

const SITES: Readonly<Record<SiteId, SiteConfig>> = {
  areyouprotected: {
    id: 'areyouprotected',
    url: 'https://areyouprotected.org',
    displayName: 'AreYouProtected.org',
    appName: 'Are You Protected?',
    appShortName: 'AreYouProtected',
    pageHeader: 'Is Your Immune System Protecting You?',
    warningSignsHeader: "How Do I Know If I'm Protected?",
    pageTitle: `Are You Protected? | ${PAGE_TITLE_SUFFIX}`,
    pageDescription:
      "Learn the 10 warning signs of Primary Immunodeficiency. 70–90% of the 10 million people living with PI remain undiagnosed. Find out if you're protected.",
    ogDescription:
      "70–90% of people with Primary Immunodeficiency remain undiagnosed. Learn the Jeffrey Modell Foundation's 10 warning signs and find out if you're protected.",
    ogImage: '/img/og/areyouprotected-1200x630.jpg',
  },
  immunenotinvisible: {
    id: 'immunenotinvisible',
    url: 'https://immunenotinvisible.org',
    displayName: 'ImmuneNotInvisible.org',
    appName: 'Immune Not Invisible',
    appShortName: 'ImmuneNotInvis',
    pageHeader: 'Is Your Immune System Working?',
    warningSignsHeader: 'How Do I Know If I Have Primary Immunodeficiency?',
    pageTitle: `Is Your Immune System Working? | ${PAGE_TITLE_SUFFIX}`,
    pageDescription:
      'Learn the 10 warning signs of Primary Immunodeficiency. 70–90% of the 10 million people living with PI remain undiagnosed. Find out if your immune system is working.',
    ogDescription:
      "70–90% of people with Primary Immunodeficiency remain undiagnosed. Learn the Jeffrey Modell Foundation's 10 warning signs and find out if your immune system is working.",
    ogImage: '/img/og/immunenotinvisible-1200x630.jpg',
  },
  answersstarthere: {
    id: 'answersstarthere',
    url: 'https://answersstarthere.org',
    displayName: 'AnswersStartHere.org',
    appName: 'Answers Start Here',
    appShortName: 'AnswersStart',
    pageHeader: 'Is Something Missing From Your Immune System?',
    warningSignsHeader: 'How Do I Know If Something Is Missing?',
    pageTitle: `Is Something Missing From Your Immune System? | ${PAGE_TITLE_SUFFIX}`,
    pageDescription:
      'Learn the 10 warning signs of Primary Immunodeficiency. 70–90% of the 10 million people living with PI remain undiagnosed. Find out if something is missing from your immune system.',
    ogDescription:
      "70–90% of people with Primary Immunodeficiency remain undiagnosed. Learn the Jeffrey Modell Foundation's 10 warning signs and find out if something is missing.",
    ogImage: '/img/og/answersstarthere-1200x630.jpg',
  },
};

const SITES_BY_ID: ReadonlyMap<string, SiteConfig> = new Map(Object.entries(SITES));

/**
 * Resolves the active site from NEXT_PUBLIC_SITE_ID.
 *
 * On Vercel (VERCEL=1) the ID is required: a project missing it would
 * otherwise ship areyouprotected.org canonical URLs on another domain and
 * Google would merge the sites. Locally, unset falls back to areyouprotected
 * for convenience. An unrecognized value always fails the build.
 */
function resolveCurrentSite(): SiteConfig {
  const rawSiteId = process.env['NEXT_PUBLIC_SITE_ID']?.trim();

  if (!rawSiteId) {
    if (process.env['VERCEL'] === '1') {
      throw new Error(
        'NEXT_PUBLIC_SITE_ID is not set for this Vercel project/environment. Set it in Project Settings → Environment Variables (Production and Preview).',
      );
    }

    return SITES.areyouprotected;
  }

  const site = SITES_BY_ID.get(rawSiteId);

  if (!site) {
    throw new Error(
      `Unknown NEXT_PUBLIC_SITE_ID "${rawSiteId}". Expected one of: ${[...SITES_BY_ID.keys()].join(', ')}`,
    );
  }

  return site;
}

export const CURRENT_SITE: SiteConfig = resolveCurrentSite();
